import { z } from 'zod';

/**
 * Subdominios reservados de *.sell.solteca.net (ADR-009): ninguna tienda puede usarlos
 * como slug porque los ocupa la propia plataforma o están reservados para el futuro.
 */
export const RESERVED_SLUGS = [
  'admin',
  'api',
  's3',
  'rtc',
  'www',
  'app',
  'mail',
  'status',
  // EP-12 (HU-044, ADR-015): subdominio del panel del administrador de la plataforma.
  'gestion',
] as const;

export type ReservedSlug = (typeof RESERVED_SLUGS)[number];

export const SLUG_MIN_LENGTH = 3;
export const SLUG_MAX_LENGTH = 30;

/** Minúsculas a-z, dígitos y guiones; sin guion al inicio ni al final. */
export const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

const reservedSet: ReadonlySet<string> = new Set(RESERVED_SLUGS);

/** Indica si el slug coincide con un subdominio reservado (comparación exacta, sin normalizar). */
export function isReservedSlug(slug: string): slug is ReservedSlug {
  return reservedSet.has(slug);
}

/**
 * Formato de slug (longitud y caracteres), SIN comprobar los reservados. El alta (HU-001)
 * lo usa porque un slug reservado se trata como "ocupado" (CU-001 RN-01b → 409 con
 * sugerencias), no como un error de formato.
 */
export const slugFormatSchema = z
  .string()
  .min(SLUG_MIN_LENGTH, { message: `Slug must be at least ${SLUG_MIN_LENGTH} characters` })
  .max(SLUG_MAX_LENGTH, { message: `Slug must be at most ${SLUG_MAX_LENGTH} characters` })
  .regex(SLUG_PATTERN, {
    message:
      'Slug may only contain lowercase letters, digits and hyphens, and cannot start or end with a hyphen',
  });

/**
 * Slug de tienda = subdominio en <slug>.sell.solteca.net.
 * No transforma la entrada (ni trim ni minúsculas): el cliente debe enviarlo ya normalizado.
 */
export const slugSchema = slugFormatSchema.refine((slug) => !isReservedSlug(slug), {
  message: 'Slug is reserved',
});

export type Slug = z.infer<typeof slugSchema>;

/** Indica si el texto es un slug válido y no reservado. */
export function isValidSlug(value: string): boolean {
  return slugSchema.safeParse(value).success;
}

/** Recorta a la longitud máxima sin dejar un guion al final. */
function clampSlug(value: string, max = SLUG_MAX_LENGTH): string {
  return value.slice(0, max).replace(/-+$/, '');
}

/**
 * Propone un slug a partir de un texto libre (el nombre de la tienda):
 * "Tienda Luna Ñandú" → "tienda-luna-nandu". Puede devolver un slug inválido (p. ej. vacío
 * o demasiado corto): quien lo use debe validarlo con slugSchema.
 */
export function slugify(text: string): string {
  const ascii = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return clampSlug(ascii);
}

/** Sufijos de las variantes que se sugieren cuando un slug está ocupado (CU-001 5.A). */
const SUGGESTION_SUFFIXES = ['tienda', 'online', 'oficial', 'store', 'shop'] as const;

/**
 * Variantes candidatas para un slug ocupado o reservado, en orden de preferencia y ya
 * validadas (formato y no reservadas). Quien las use debe filtrar las que estén ocupadas.
 */
export function slugCandidates(base: string, count = 12): string[] {
  const root = slugify(base) || 'mi-tienda';
  const variants: string[] = [];
  for (const suffix of SUGGESTION_SUFFIXES) {
    variants.push(`${clampSlug(root, SLUG_MAX_LENGTH - suffix.length - 1)}-${suffix}`);
  }
  for (let n = 1; variants.length < count + SUGGESTION_SUFFIXES.length && n <= 99; n += 1) {
    const suffix = String(n);
    variants.push(`${clampSlug(root, SLUG_MAX_LENGTH - suffix.length - 1)}-${suffix}`);
  }
  const unique = [...new Set(variants)].filter((slug) => slug !== root && isValidSlug(slug));
  return unique.slice(0, count);
}
